import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, User, Wrench, Calendar, RotateCcw } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

const Assignments = () => {
  const [assignments, setAssignments] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    tool_id: '',
    collaborator_id: '',
    quantity: 1,
    rental_duration_days: '',
    assignment_type: 'new',
    replacement_reason: '',
    old_tool_id: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [assignmentsRes, collabsRes, toolsRes] = await Promise.all([
        axios.get(`${API}/assignments/active`),
        axios.get(`${API}/collaborators`),
        axios.get(`${API}/tools`),
      ]);
      setAssignments(assignmentsRes.data);
      setCollaborators(collabsRes.data);
      setTools(toolsRes.data.filter(t => t.quantity_available > 0));
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        tool_id: formData.tool_id,
        collaborator_id: formData.collaborator_id,
        quantity: parseInt(formData.quantity),
        rental_duration_days: formData.rental_duration_days ? parseInt(formData.rental_duration_days) : null,
        assignment_type: formData.assignment_type,
        replacement_reason: formData.assignment_type === 'replacement' ? formData.replacement_reason : null,
        old_tool_id: formData.assignment_type === 'replacement' ? formData.old_tool_id : null,
      };
      await axios.post(`${API}/assignments`, payload);
      toast.success('Outil affecté avec succès');
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    }
  };

  const handleReturn = async (assignmentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir marquer cet outil comme retourné ?')) return;
    try {
      await axios.post(`${API}/assignments/${assignmentId}/return`);
      toast.success('Outil retourné avec succès');
      loadData();
    } catch (error) {
      toast.error('Erreur lors du retour');
    }
  };

  const resetForm = () => {
    setFormData({
      tool_id: '',
      collaborator_id: '',
      quantity: 1,
      rental_duration_days: '',
      assignment_type: 'new',
      replacement_reason: '',
      old_tool_id: '',
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR');
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return null;
    const now = new Date();
    const end = new Date(endDate);
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg font-medium text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight leading-none">
            Affectations Actives
          </h1>
          <p className="text-muted-foreground mt-2">
            Gérez les affectations d'outils aux collaborateurs
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-assignment-button"
              className="rounded-full shadow-lg shadow-primary/20"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle Affectation
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Nouvelle Affectation</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="collaborator">Collaborateur *</Label>
                <Select
                  value={formData.collaborator_id}
                  onValueChange={(value) => setFormData({ ...formData, collaborator_id: value })}
                  required
                >
                  <SelectTrigger id="collaborator" data-testid="assignment-collaborator-select" className="rounded-lg">
                    <SelectValue placeholder="Sélectionner un collaborateur" />
                  </SelectTrigger>
                  <SelectContent>
                    {collaborators.map((collab) => (
                      <SelectItem key={collab.id} value={collab.id}>
                        {collab.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tool">Outil *</Label>
                <Select
                  value={formData.tool_id}
                  onValueChange={(value) => setFormData({ ...formData, tool_id: value })}
                  required
                >
                  <SelectTrigger id="tool" data-testid="assignment-tool-select" className="rounded-lg">
                    <SelectValue placeholder="Sélectionner un outil" />
                  </SelectTrigger>
                  <SelectContent>
                    {tools.map((tool) => (
                      <SelectItem key={tool.id} value={tool.id}>
                        {tool.name} - {tool.type} ({tool.quantity_available} dispo.)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="quantity">Quantité *</Label>
                <Input
                  id="quantity"
                  data-testid="assignment-quantity-input"
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  required
                  className="rounded-lg"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="assignment_type">Type d'affectation *</Label>
                <Select
                  value={formData.assignment_type}
                  onValueChange={(value) => setFormData({ ...formData, assignment_type: value })}
                  required
                >
                  <SelectTrigger id="assignment_type" data-testid="assignment-type-select" className="rounded-lg">
                    <SelectValue placeholder="Sélectionner le type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">Nouvel outil</SelectItem>
                    <SelectItem value="replacement">Remplacement (Perte/Usure)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.assignment_type === 'replacement' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="old_tool">Ancien outil *</Label>
                    <Select
                      value={formData.old_tool_id}
                      onValueChange={(value) => setFormData({ ...formData, old_tool_id: value })}
                      required
                    >
                      <SelectTrigger id="old_tool" data-testid="old-tool-select" className="rounded-lg">
                        <SelectValue placeholder="Sélectionner l'ancien outil" />
                      </SelectTrigger>
                      <SelectContent>
                        {tools.map((tool) => (
                          <SelectItem key={tool.id} value={tool.id}>
                            {tool.name} - {tool.type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="replacement_reason">Raison du remplacement *</Label>
                    <Select
                      value={formData.replacement_reason}
                      onValueChange={(value) => setFormData({ ...formData, replacement_reason: value })}
                      required
                    >
                      <SelectTrigger id="replacement_reason" data-testid="replacement-reason-select" className="rounded-lg">
                        <SelectValue placeholder="Sélectionner la raison" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Perte">Perte</SelectItem>
                        <SelectItem value="Casse">Casse</SelectItem>
                        <SelectItem value="Usure">Usure</SelectItem>
                        <SelectItem value="Endommagé">Endommagé</SelectItem>
                        <SelectItem value="Obsolète">Obsolète</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="duration">Durée de location (jours)</Label>
                <Input
                  id="duration"
                  data-testid="assignment-duration-input"
                  type="number"
                  min="1"
                  placeholder="Optionnel"
                  value={formData.rental_duration_days}
                  onChange={(e) => setFormData({ ...formData, rental_duration_days: e.target.value })}
                  className="rounded-lg"
                />
              </div>

              <Button
                data-testid="assignment-submit-button"
                type="submit"
                className="w-full rounded-full"
              >
                Affecter
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {assignments.map((assignment) => {
          const daysRemaining = getDaysRemaining(assignment.end_date);
          const isExpiring = daysRemaining !== null && daysRemaining <= 7;

          return (
            <Card
              key={assignment.id}
              data-testid={`assignment-card-${assignment.id}`}
              className={`hover:shadow-lg transition-shadow duration-200 ${
                isExpiring ? 'border-destructive border-2' : ''
              }`}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <CardTitle className="text-lg font-heading">Affectation Active</CardTitle>
                  {isExpiring && (
                    <Badge variant="destructive" data-testid={`expiring-badge-${assignment.id}`}>
                      Expire bientôt
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {assignment.collaborator?.name?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{assignment.collaborator?.name}</span>
                    </div>
                    {assignment.collaborator?.email && (
                      <p className="text-sm text-muted-foreground">{assignment.collaborator.email}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">{assignment.tool?.name}</span>
                  <span className="text-sm text-muted-foreground">({assignment.tool?.type})</span>
                  <Badge variant="secondary" className="ml-auto">x{assignment.quantity}</Badge>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Affecté le:</span>
                    <span>{formatDate(assignment.assigned_date)}</span>
                  </div>
                  {assignment.end_date && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Fin prévue:</span>
                      <span>{formatDate(assignment.end_date)}</span>
                      {daysRemaining !== null && (
                        <Badge variant="outline" className="ml-auto">
                          {daysRemaining > 0 ? `${daysRemaining}j restants` : 'Expiré'}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>

                <Button
                  data-testid={`return-assignment-${assignment.id}`}
                  onClick={() => handleReturn(assignment.id)}
                  variant="outline"
                  className="w-full rounded-full"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Marquer comme retourné
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {assignments.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">Aucune affectation active</p>
        </div>
      )}
    </div>
  );
};

export default Assignments;